import { useAuthContext } from "services/authentication/AuthenticationContext";

/*
 * permissionChecks.ts
 * Custom hooks which can be used to check if a certain feature is active in the
 * current package of the environment, if the current user is allowed to see a
 * feature based on its role or if a current user is allowed for a certain right.
 */

// Checks if the feature is available in the current package of the environment
export const usePackageFeature = (featureName: string): boolean => {
    const { packageFeatures = [] } = useAuthContext();
    let isEnabled = false;

    if (packageFeatures.includes(featureName)) {
        isEnabled = true;
    }
    
    return isEnabled;
};


// Feature context needed to check whether a feature is active, usable outside of hooks
export interface FeatureCheckContext {
    activeFeatures: string[];
    roleFeatures: string[];
    allowedFeatures: string[];
}

// Plain function version of the active feature check, usable inside loops such as .filter()
// where hooks cannot be called
export const checkFeatureActive = (featureName: string, { activeFeatures, roleFeatures, allowedFeatures }: FeatureCheckContext): boolean => {
    const isActive = activeFeatures.includes(featureName);
    const isRoleSpecific = roleFeatures.includes(featureName);

    // If the feature is role specific, check if the feature is in activeFeatures and allowedFeatures
    if (isRoleSpecific) {
        return isActive && allowedFeatures.includes(featureName);
    }

    // If the feature is not role specific, check if the feature is in activeFeatures
    return isActive;
};

// Checks a required feature, or an array of features treated as an OR condition, against the active features
export const checkRequiredFeatureActive = (requiredFeature: string | string[] | undefined, context: FeatureCheckContext): boolean => {
    if (!requiredFeature) {
        return true;
    }

    const features = Array.isArray(requiredFeature) ? requiredFeature : [requiredFeature];
    return features.some(feature => checkFeatureActive(feature, context));
};

// Checks if the feature is active in the environment and if the current user
// is allowed to see it based the allowed features in its role
export const useActiveFeature = (featureName: string): boolean => {
    const { activeFeatures = [], roleFeatures = [], allowedFeatures = [] } = useAuthContext();

    return checkFeatureActive(featureName, { activeFeatures, roleFeatures, allowedFeatures });
};


// Checks if the current user has permissions for a certain right
export const useAllowedRight = (rightName: string): boolean => {
    const { allowedRights = [] } = useAuthContext();
    return allowedRights.includes(rightName);
}

// Checks if the allowed rights contain at least one of the given rights
export const hasAnyAllowedRight = (allowedRights: string[], permissionCheck: string | string[]): boolean => {
    const rights = Array.isArray(permissionCheck) ? permissionCheck : [permissionCheck];
    return rights.some(right => allowedRights.includes(right));
}

// Checks if the view only package restrictions apply, superusers and staff users are exempt
export const useViewOnlyPackageRestriction = (): boolean => {
    const { packageIsViewOnly = false, allowedRights = [] } = useAuthContext();
    return packageIsViewOnly && !hasAnyAllowedRight(allowedRights, ['no_user', 'staff_user']);
}

// Helper function to filter the options to only the curently active, allowed and released features
export const useFilteredDropdownOptionsByFeature = (
    dropdownOptions: { value: string; name: string; requiredFeature?: string }[]
) => {
    const { packageFeatures, activeFeatures, allowedFeatures } = useAuthContext();
    
    return dropdownOptions.filter(dropdownOption => {

        // If the required feature prop is given, check if it is active, allowed and released
        if (dropdownOption.requiredFeature) {
            return (
                activeFeatures.includes(dropdownOption.requiredFeature) &&
                allowedFeatures.includes(dropdownOption.requiredFeature) &&
                packageFeatures.includes(dropdownOption.requiredFeature)
            );
        }

        // If no required feature prop is given, always include the option
        return true;
    });
};